import useWidgetViewManager from '@/composables/widget-view-manager'
import { useWorkspace } from '@/composables/workflow'
import { until } from '@vueuse/core'
import useWebsocket from '@/api/websocket'
import { updateSentryContext } from '@/lib/sentry'
import { setupTracker } from '@/composables/tracker'
import { updateOrchestratorApi } from '@/api/orchestrator'
import { updateAppsApi } from '@/api/apps'
import { promiseTimeout } from '@vueuse/core'
import { useI18n } from '../i18n'
import useEmbed from '@/embed/code'
import { readonly, ref } from 'vue'
import type { WidgetOptions } from '@/types'
import useWidgetOptions from '@/composables/widget-options'
import { useCustomCss } from '../custom-css'

const INITIALIZATION_NO_CONTEXT = 'initialization-no-context'

const preInitialized = ref(false)
const preInitializing = ref(false)
const initialized = ref(false)
const widgetOpened = ref(false)
const initializing = ref(false)

function useWidgetHooks() {
    const preInitialize = async (token: string, options?: Partial<WidgetOptions>, clientDomain?: string) => {
        const workspace = useWorkspace()
        const { setOptions } = useWidgetOptions()
        const { setupCustomCss } = useCustomCss()
        const { initialize: initializeWidgetViewManager } = useWidgetViewManager()
        const { changeLang } = useI18n()

        try {
            if (preInitialized.value) {
                throw new Error('already preinitialized')
            }

            if (preInitializing.value) {
                return
            }

            if (!token) {
                throw INITIALIZATION_NO_CONTEXT
            }

            preInitializing.value = true

            const { caseId, partyId, workflowDefinitionId, lang } = workspace.setToken(token)

            if (options) {
                setOptions(options)
            }

            const mode = workspace.mode.value
            updateSentryContext(caseId || partyId, workflowDefinitionId)

            setupTracker(caseId || partyId, workflowDefinitionId, mode, clientDomain)

            updateOrchestratorApi(mode)
            updateAppsApi(mode)

            initializeWidgetViewManager()

            await Promise.all([
                setupCustomCss(),
                changeLang(lang),
            ])

            preInitialized.value = true
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e)
            handleInitializationError(e)
        } finally {
            preInitializing.value = false
        }
    }

    const handleInitializationError = (e: unknown) => {
        const { t } = useI18n()
        const { showError } = useWidgetViewManager()
        const { emitErrorEvent } = useEmbed()

        if (e === INITIALIZATION_NO_CONTEXT) {
            showError({
                title: t('views.widget.no_context.title'),
                description: t('views.widget.no_context.description'),
                retry: false,
                svgIcon: 'bug.svg',
            })
            emitErrorEvent({
                code: 'fail',
                message: 'Initialization failed',
            })
            return
        }

        if (e === 'already preinitialized') {
            emitErrorEvent({
                code: 'fail',
                message: 'Already preinitialized',
            })
            return
        }

        showError({
            title: t('views.widget.initialization_failed.title'),
            description: t('views.widget.initialization_failed.description'),
            retry: true,
            svgIcon: 'bug.svg',
            error: e,
        })
        emitErrorEvent({
            code: 'fail',
            message: 'No Context',
        })
    }

    const initialize = async () => {
        const { initialize: initializeWebsocket, firstMessage } = useWebsocket()
        const { startWidgetManager, handleWorkflowTimeout } = useWidgetViewManager()

        const { token, workflowId, getCurrentWorkflow, startWorkflow, mode } = useWorkspace()

        if (!token.value) {
            return
        }

        try {
            if (!preInitialized.value && !token.value) {
                throw new Error('Run transactionlink.setOptions({ token }) first')
                return
            }

            if (initialized.value || initializing.value || widgetOpened.value) {
                return
            }

            initializing.value = true

            setWidgetOpened(true)

            await getCurrentWorkflow()

            if (!token.value || !workflowId.value) return

            await initializeWebsocket(token.value, `${workflowId.value}`, mode.value)

            await startWorkflow()

            handleWorkflowTimeout()

            // wait for first message to display first app
            await until(firstMessage)
                .toBeTruthy({ timeout: 600 * 1000, throwOnTimeout: true })

            if (firstMessage.value) {
                startWidgetManager(firstMessage.value)
            }

            initialized.value = true
            // bypass for reopening widget with defined token
            preInitialized.value = true
        } catch (error) {
            handleInitializationError(error)
        } finally {
            initializing.value = false
        }

    }

    const reinitialize = async () => {
        if (!initialized.value) return

        resetInitialization()

        await promiseTimeout(1000)
        await initialize()
    }

    const resetInitialization = async () => {
        const { reset: resetWidgetViewManager } = useWidgetViewManager()

        resetWidgetViewManager()
        preInitialized.value = false
        initialized.value = false
        setWidgetOpened(false)
    }

    const initializeCustomPagePreview = () => {
        const { initialize: initializeWidgetViewManager } = useWidgetViewManager()
        const { setupCustomCssForPreview } = useCustomCss()
        initializeWidgetViewManager()
        setWidgetOpened(true)
        setTimeout(() => {
            setupCustomCssForPreview()
        }, 0)
    }

    const setWidgetOpened = (val: boolean) => widgetOpened.value = val

    return {
        preInitialized,
        preInitialize,
        initialized,
        initialize,
        reinitialize,
        resetInitialization,
        initializeCustomPagePreview,
        widgetOpened: readonly(widgetOpened),
    }

}

export default useWidgetHooks
